import type {
  ActionRequirement,
  ActionResultProjection,
  ActionRunAccess,
  ActionScope,
  AuthoredCap,
  Cap,
} from "@manifold/protocol";
import type { z } from "zod";

/**
 * An action a plugin declares: one named, cap-guarded, schema'd door into a mutation.
 *
 * `input` and `result` are zod schemas rather than TypeScript types alone because the door
 * validates on the way in and on the way out, and the SAME schemas are published as JSON
 * Schema in the roster — a stranger's agent reads the contract the dispatcher enforces,
 * never a hand-written description of it.
 *
 * `caps` is what invoking this action requires of the CALLER; it must be a subset of the
 * declaring manifest's `capabilities`, so a manifest is a readable ceiling on a plugin's
 * authority (checked by `assembleRoster`, refused loudly if violated).
 *
 * What an action does NOT declare is what it EMITS. An event kind is declared once, on the
 * MANIFEST (`contributes.events`), and reached through `ctx.emit` inside the handler — because
 * the same kind is emitted at several doors (a container is created by one action and deleted
 * by another, both saying something about the same node), and a per-action list would be the
 * manifest's vocabulary restated once per door, drifting one door at a time.
 */
export interface ActionDef<In = unknown, Out = unknown> {
  /**
   * LOCAL name (`rename`), never the full one: the composition prefixes the owning plugin's
   * id, so a plugin cannot name a door outside its own namespace.
   */
  readonly name: string;
  readonly title: string;
  /**
   * What the CALLER must hold: one of the engine's capabilities, or one this plugin declared
   * in its own namespace (`<pluginId>:<name>`, ADR 0035). `delegates` below stays the engine's
   * closed set — a delegate is a NATIVE API ceiling, and the native APIs are the engine's.
   */
  readonly caps: readonly AuthoredCap[];
  /**
   * Native job/service capability ceiling, not caller permission or target admission.
   * Each native call resolves concrete targets and checks the original caller's authority
   * and resource consent. Must be a subset of the manifest's capabilities.
   */
  readonly delegates?: readonly Cap[];
  readonly input: z.ZodType<In>;
  readonly result: z.ZodType<Out>;
  /** Explicit, bounded publication policy; absent means ordinary results stay private. */
  readonly resultProjection?: ActionResultProjection;
  /**
   * Marks a CLEANUP action: one that removes things and therefore keeps working while the
   * plugin is disabled (D12 — creation and administration die, cleanup survives). The
   * dispatcher skips only the `plugin_disabled` rung for it; caps and schemas still apply.
   */
  readonly cleanup?: boolean;
  /**
   * A reserved core identity action's autonomous-run lifecycle exception. Policy
   * acknowledgement, teardown and inspection stay reachable while ordinary authority is
   * suspended; delegation is graded at the requested child target by the identity mechanism.
   * Assembly refuses this metadata outside `core.access`.
   */
  readonly runAccess?: ActionRunAccess;
  /** Active autonomous callers must supply a safe declaration, never additional authority. */
  readonly agentJustification?: "required";
  /**
   * The authority grade this door is written for; absent ≡ `"workspace"`.
   *
   * `"container"` declares that the action's whole effect is confined to ONE container, which is
   * what lets a container-scoped token through the scope rung. The container is the TOKEN's
   * (`ctx.containerScope`), never an argument. For context-targeted doors the cap rung
   * precedes argument validation. Doors declaring `requirements` instead validate bounded
   * reference targets before evaluating authority at each node; the token's container
   * ceiling still applies to every target.
   *
   * It is a CONTRACT on the handler, not a label: with a non-null `ctx.containerScope` the handler
   * MUST refuse anything outside that container. The rung can only prove the caller's caps hold
   * for its own container; whether the thing named in the arguments lives there is a question only
   * the handler can ask.
   */
  readonly scope?: ActionScope;
  /** When present, every declared cap is discharged at validated input references, not the context anchor. */
  readonly requirements?: readonly ActionRequirement[];
  /** Opaque doors never persist caller input, even on malformed/refused dispatches. */
  readonly trace?: "redacted" | "opaque";
}

/**
 * An action of unknown shape, as a registry holds it. zod v4 declares `ZodType`'s parameters
 * covariant (`out Output`, `out Input`), so every `ActionDef<In, Out>` assigns to this without
 * a cast and without `any`: a dispatcher that only calls `input.safeParse` / `result.parse`
 * needs no more type than "some schema".
 */
export type AnyActionDef = ActionDef;

/**
 * Identity helper. It exists purely so `In`/`Out` are inferred from the schemas at the
 * definition site — a handler written against `defineAction`'s result gets its argument and
 * return types checked against the same schemas the door enforces at runtime.
 */
export function defineAction<In, Out>(def: ActionDef<In, Out>): ActionDef<In, Out> {
  return def;
}
